import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Menu,
  Calendar as CalendarIcon,
  CheckCircle2,
  ChevronRight,
  Lightbulb,
  ClipboardList,
  BookOpen,
  CalendarDays,
  History,
  Star,
  LineChart,
  Info as InfoIcon,
  ShieldCheck,
  Target,
} from "lucide-react";

import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import { FaqSchema, FaqList, OrganizationAlt } from "@/components/SeoContent";
import { api } from "@/lib/api";

// -------------------- helpers --------------------
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const localTodayDMY = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// "25/08/2026" -> "25 August 2026"
const prettyDate = (dmy) => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/");
  const mi = parseInt(m, 10) - 1;
  if (mi < 0 || mi > 11) return dmy;
  return `${parseInt(d, 10)} ${MONTHS[mi]} ${y}`;
};

// -------------------- top mobile bar --------------------
function HomeHeader({ onMenu }) {
  return (
    <header
      className="sticky top-0 z-50 bg-white border-b border-black/5"
      data-testid="site-header"
    >
      <div className="max-w-md mx-auto sm:max-w-2xl lg:max-w-4xl px-4 sm:px-6">
        <div className="h-16 flex items-center justify-between gap-3">
          <Link
            to="/"
            className="flex items-center gap-2.5 min-w-0"
            data-testid="header-logo-link"
          >
            <span className="w-9 h-9 rounded-full grid place-items-center shrink-0">
              <TargetLogo className="w-9 h-9" />
            </span>
            <span className="font-display font-black tracking-tight leading-none text-lg sm:text-xl truncate">
              <span className="text-black">JUWAI</span>{" "}
              <span className="text-[#F04E1E]">MORNING TEER</span>
            </span>
          </Link>
          <button
            onClick={onMenu}
            aria-label="Open menu"
            className="p-2 rounded-lg hover:bg-black/5 transition-colors"
            data-testid="header-menu-toggle"
          >
            <Menu className="w-6 h-6 text-black" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </header>
  );
}

// simple SVG target used in logo & cards
function TargetLogo({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <circle cx="32" cy="32" r="30" fill="#fff" stroke="#111" strokeWidth="3" />
      <circle cx="32" cy="32" r="22" fill="#fff" stroke="#111" strokeWidth="3" />
      <circle cx="32" cy="32" r="14" fill="#F04E1E" stroke="#111" strokeWidth="3" />
      <circle cx="32" cy="32" r="5" fill="#111" />
      {/* arrow */}
      <path
        d="M6 6 L34 34"
        stroke="#F04E1E"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path d="M6 6 l0 8 l8 0 z" fill="#F04E1E" />
      <path d="M6 6 l8 0 l0 8 z" fill="#F04E1E" />
    </svg>
  );
}

// -------------------- side drawer menu --------------------
function SideMenu({ open, onClose }) {
  const links = [
    { to: "/", label: "Home" },
    { to: "/previous-results", label: "Previous Results" },
    { to: "/common-numbers", label: "Common Numbers" },
    { to: "/juwai-morning-teer", label: "Juwai Teer Information" },
    { to: "/historical-results", label: "Historical Results" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
    { to: "/disclaimer", label: "Disclaimer" },
  ];
  return (
    <div
      className={`fixed inset-0 z-[60] transition ${open ? "" : "pointer-events-none"}`}
      aria-hidden={!open}
    >
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />
      <aside
        className={`absolute right-0 top-0 h-full w-72 bg-white shadow-2xl transform transition-transform ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="side-menu"
      >
        <div className="p-5 border-b border-black/5 flex items-center gap-2">
          <TargetLogo className="w-8 h-8" />
          <span className="font-display font-black text-black">
            JUWAI <span className="text-[#F04E1E]">MORNING TEER</span>
          </span>
        </div>
        <nav className="p-4 flex flex-col">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={onClose}
              className="px-3 py-3 rounded-lg text-sm font-semibold text-slate-800 hover:bg-orange-50"
            >
              {l.label}
            </Link>
          ))}
        </nav>
      </aside>
    </div>
  );
}

// -------------------- illustrated icons --------------------
function LightbulbIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 100 120" className={className} aria-hidden="true">
      {/* rays */}
      <g stroke="#FFF3B0" strokeWidth="4" strokeLinecap="round">
        <line x1="50" y1="6" x2="50" y2="14" />
        <line x1="14" y1="42" x2="6" y2="42" />
        <line x1="86" y1="42" x2="94" y2="42" />
        <line x1="22" y1="14" x2="16" y2="8" />
        <line x1="78" y1="14" x2="84" y2="8" />
      </g>
      {/* bulb glass */}
      <path
        d="M50 16c-16 0-27 12-27 26 0 10 6 16 10 20 3 4 5 6 5 12h24c0-6 2-8 5-12 4-4 10-10 10-20 0-14-11-26-27-26z"
        fill="#FFD84D"
        stroke="#111"
        strokeWidth="3"
      />
      {/* filament */}
      <path d="M40 42c4 6 6 8 10 8s6-2 10-8" fill="none" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round" />
      {/* base */}
      <rect x="36" y="74" width="28" height="8" rx="2" fill="#374151" />
      <rect x="38" y="82" width="24" height="6" rx="2" fill="#4B5563" />
      <rect x="42" y="88" width="16" height="8" rx="4" fill="#1F2937" />
      {/* highlight */}
      <ellipse cx="42" cy="30" rx="4" ry="8" fill="#FFF9CC" opacity="0.85" />
    </svg>
  );
}

function ClipboardCheckIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 100 120" className={className} aria-hidden="true">
      {/* board */}
      <rect x="14" y="18" width="72" height="94" rx="8" fill="#FFFFFF" stroke="#111" strokeWidth="3" />
      {/* clip */}
      <rect x="36" y="10" width="28" height="16" rx="4" fill="#9CA3AF" stroke="#111" strokeWidth="3" />
      <rect x="42" y="6" width="16" height="8" rx="3" fill="#D1D5DB" stroke="#111" strokeWidth="2.5" />
      {/* rows with checks */}
      {[42, 62, 82].map((y, i) => (
        <g key={i}>
          <circle cx="28" cy={y} r="6" fill="#16A34A" />
          <path d={`M25 ${y} l3 3 5-6`} stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="40" y={y - 3} width="36" height="6" rx="2" fill="#93C5FD" />
        </g>
      ))}
    </svg>
  );
}

function OpenBookIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 120 100" className={className} aria-hidden="true">
      {/* spine shadow */}
      <path d="M60 20 L60 92" stroke="#0F5A2E" strokeWidth="3" />
      {/* left page */}
      <path
        d="M10 22 C 28 16, 46 16, 58 24 L58 92 C 46 84, 28 84, 10 90 Z"
        fill="#FFFFFF"
        stroke="#111"
        strokeWidth="3"
      />
      {/* right page */}
      <path
        d="M110 22 C 92 16, 74 16, 62 24 L62 92 C 74 84, 92 84, 110 90 Z"
        fill="#FFFFFF"
        stroke="#111"
        strokeWidth="3"
      />
      {/* lines */}
      <g stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round">
        <line x1="18" y1="34" x2="52" y2="30" />
        <line x1="18" y1="44" x2="52" y2="40" />
        <line x1="18" y1="54" x2="52" y2="50" />
        <line x1="68" y1="30" x2="102" y2="34" />
        <line x1="68" y1="40" x2="102" y2="44" />
        <line x1="68" y1="50" x2="102" y2="54" />
      </g>
    </svg>
  );
}

// -------------------- feature cards --------------------
function FeatureCard({ to, title, tone, IconComp, testId }) {
  // tone: 'orange' | 'blue' | 'green'
  const tones = {
    orange: { border: "border-[#F04E1E]", body: "bg-[#F04E1E]" },
    blue:   { border: "border-[#2C74D4]", body: "bg-[#2C74D4]" },
    green:  { border: "border-[#1D8B48]", body: "bg-[#1D8B48]" },
  }[tone];

  return (
    <Link
      to={to}
      className={`rounded-2xl border-[2px] ${tones.border} bg-white overflow-hidden shadow-[0_10px_22px_-14px_rgba(0,0,0,0.35)] block`}
      data-testid={testId}
    >
      {/* TOP white: centered target + brand */}
      <div className="px-1.5 pt-2.5 pb-2 bg-white flex flex-col items-center gap-0.5">
        <TargetLogo className="w-8 h-8" />
        <div className="font-display font-black leading-[1] tracking-tight text-center">
          <div className="text-black text-[13px] sm:text-[15px]">JUWAI</div>
          <div className="text-[#F04E1E] text-[10px] sm:text-[12px] mt-0.5 whitespace-nowrap">
            MORNING TEER
          </div>
        </div>
      </div>
      {/* BOTTOM color: icon on top, label centered below */}
      <div className={`${tones.body} px-1.5 py-3 flex flex-col items-center justify-center gap-2 min-h-[130px]`}>
        <IconComp className="w-12 h-12 sm:w-14 sm:h-14 shrink-0" />
        <div className="font-display font-black text-white text-center leading-[1.05] tracking-tight text-[13px] sm:text-[16px]">
          {title}
        </div>
      </div>
    </Link>
  );
}

// -------------------- explore-more pill --------------------
function ExploreItem({ to, label, Icon, tint, testId }) {
  // tint: hex background of icon bubble
  return (
    <Link
      to={to}
      className="rounded-2xl border border-black/5 bg-white p-3 shadow-[0_4px_12px_-6px_rgba(0,0,0,0.15)] flex flex-col items-center text-center gap-2"
      data-testid={testId}
    >
      <span
        className="w-11 h-11 rounded-full grid place-items-center shrink-0"
        style={{ backgroundColor: tint }}
      >
        <Icon className="w-5 h-5 text-white" strokeWidth={2.4} />
      </span>
      <span className="font-display font-black text-[11px] sm:text-[12px] leading-[1.1] text-slate-800 break-words">
        {label}
      </span>
    </Link>
  );
}

// -------------------- big result circle --------------------
function ResultCircle({ label, time, value }) {
  return (
    <div
      className="flex flex-col items-center gap-1.5"
      data-testid={`result-${label.toLowerCase().replace("/", "")}`}
    >
      <div className="text-xs font-bold text-black">
        <span className="text-black">{label}</span>{" "}
        <span className="text-slate-600 font-semibold">({time})</span>
      </div>
      <div
        className={`num-font w-16 h-16 sm:w-20 sm:h-20 rounded-full grid place-items-center border-[3px] ${
          value ? "border-[#F04E1E] bg-white" : "border-dashed border-slate-300 bg-white"
        }`}
      >
        <span
          className={`text-3xl sm:text-4xl font-black tracking-tight ${
            value ? "text-black" : "text-slate-400"
          }`}
          data-testid={`result-${label.toLowerCase().replace("/", "")}-value`}
        >
          {value ?? "--"}
        </span>
      </div>
    </div>
  );
}

// -------------------- page --------------------
export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);

  const { data: siteToday } = useQuery({
    queryKey: ["site-today"],
    queryFn: async () => (await api.get("/site/today")).data,
    staleTime: 60 * 60 * 1000,
  });
  const { data: latestData } = useQuery({
    queryKey: ["latest-result"],
    queryFn: async () => (await api.get("/results/latest")).data,
    staleTime: 30_000,
  });

  const today = siteToday?.date || localTodayDMY();
  const published = !!latestData?.published;
  const latest = latestData?.result || null;

  return (
    <>
      <Seo
        title="Juwai Morning Teer Result Today | Juwai Teer Morning Result"
        description="Check the latest Juwai Morning Teer Result Today with F/R (10:30 AM) and S/R (11:30 AM). Previous results archive, common numbers, and informational content — for reference purposes only."
        path="/"
        breadcrumbs={[{ name: "Home", path: "/" }]}
      />

      <div className="min-h-screen bg-[#FAF7F2]">
        <HomeHeader onMenu={() => setMenuOpen(true)} />
        <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

        {/* ============ HERO ============ */}
        <section
          className="relative w-full overflow-hidden"
          data-testid="hero-section"
        >
          {/* sunset/mountain gradient bg */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, #FDE7C5 0%, #F9C989 32%, #F5A66D 55%, #D67A5A 75%, #7A4A3E 100%)",
            }}
            aria-hidden="true"
          />
          {/* mountain silhouettes */}
          <svg
            className="absolute inset-x-0 bottom-0 w-full h-40 sm:h-56"
            viewBox="0 0 400 160"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d="M0 130 L60 70 L110 100 L170 40 L230 90 L290 50 L360 100 L400 80 L400 160 L0 160 Z" fill="#7C6B87" opacity="0.55" />
            <path d="M0 150 L50 100 L110 130 L180 85 L240 120 L300 95 L360 130 L400 110 L400 160 L0 160 Z" fill="#4E3F5E" opacity="0.75" />
            <path d="M0 160 L80 130 L160 150 L240 135 L320 155 L400 140 L400 160 Z" fill="#2E2436" />
          </svg>
          {/* sun glow */}
          <div
            className="absolute left-[35%] top-16 w-56 h-56 rounded-full blur-2xl opacity-70"
            style={{ background: "radial-gradient(circle, #FFE9A8 0%, rgba(255,220,140,0) 70%)" }}
            aria-hidden="true"
          />
          {/* big target on right */}
          <svg
            className="absolute right-[-40px] top-6 w-[280px] h-[280px] sm:right-4 sm:w-[340px] sm:h-[340px] drop-shadow-2xl"
            viewBox="0 0 200 200"
            aria-hidden="true"
          >
            <circle cx="100" cy="100" r="94" fill="#F8F1E6" stroke="#5A3A19" strokeWidth="4" />
            <circle cx="100" cy="100" r="78" fill="#111" />
            <circle cx="100" cy="100" r="62" fill="#2C7CD1" />
            <circle cx="100" cy="100" r="46" fill="#E23B2B" />
            <circle cx="100" cy="100" r="30" fill="#F5D34E" />
            <circle cx="100" cy="100" r="14" fill="#E23B2B" />
            {/* arrows */}
            <g stroke="#5A3A19" strokeWidth="3" strokeLinecap="round" fill="#B8321F">
              <line x1="100" y1="100" x2="18" y2="30" stroke="#4A2E14" strokeWidth="5" />
              <line x1="100" y1="100" x2="10" y2="42" stroke="#4A2E14" strokeWidth="5" />
              <line x1="100" y1="100" x2="24" y2="50" stroke="#4A2E14" strokeWidth="5" />
              <polygon points="18,30 24,22 28,30 20,36" fill="#E23B2B" />
              <polygon points="10,42 16,34 20,42 12,48" fill="#E23B2B" />
              <polygon points="24,50 30,42 34,50 26,56" fill="#E23B2B" />
            </g>
          </svg>
          {/* subtle white fade for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-white/30 to-transparent" aria-hidden="true" />

          <div className="relative max-w-md mx-auto sm:max-w-2xl lg:max-w-4xl px-4 sm:px-6 pt-6 pb-28 sm:pt-10 sm:pb-36">
            <h1
              className="font-display font-black tracking-tight text-4xl sm:text-5xl leading-[1.05] text-black"
              data-testid="home-h1"
            >
              Juwai Morning Teer
              <br />
              <span className="text-[#F04E1E]">Result Today</span>
              <svg
                viewBox="0 0 260 14"
                className="mt-1 w-64 h-3"
                aria-hidden="true"
              >
                <path
                  d="M2 8 C 70 -2, 160 14, 258 4"
                  stroke="#F04E1E"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </h1>
          </div>
        </section>

        {/* ============ LATEST RESULT CARD (overlaps hero) ============ */}
        <div className="max-w-md mx-auto sm:max-w-2xl lg:max-w-4xl px-4 sm:px-6 -mt-16 sm:-mt-20 relative z-10">
          <div
            className="rounded-3xl bg-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.25)] border border-black/5 overflow-hidden"
            data-testid="latest-result-card"
          >
            {/* orange top bar */}
            <div className="bg-[#F04E1E] px-4 py-2 flex items-center justify-between">
              <span className="text-white font-display font-black tracking-wide text-sm">
                LATEST RESULT
              </span>
              <span
                className="inline-flex items-center gap-1.5 bg-white text-black text-[10px] font-bold rounded-full px-2.5 py-1"
                data-testid="latest-result-status"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                Live Board
              </span>
            </div>

            <div className="px-3 pt-3 pb-3">
              {/* date */}
              <div
                className="flex items-center justify-center gap-1.5 text-black font-display font-bold text-sm"
                data-testid="latest-result-date"
              >
                <CalendarIcon className="w-4 h-4 text-black" strokeWidth={2.4} />
                <span>{prettyDate(today)}</span>
              </div>

              {/* two columns with divider */}
              <div className="mt-3 grid grid-cols-2 relative">
                <div className="absolute inset-y-1 left-1/2 -translate-x-1/2 w-px bg-slate-200" />
                <div className="flex justify-center">
                  <ResultCircle
                    label="F/R"
                    time="10:30 AM"
                    value={published && latest?.fr ? latest.fr : null}
                  />
                </div>
                <div className="flex justify-center">
                  <ResultCircle
                    label="S/R"
                    time="11:30 AM"
                    value={published && latest?.sr ? latest.sr : null}
                  />
                </div>
              </div>

              {/* status pill (only 'Waiting for Result' — 'Result Published' removed per user request) */}
              {!published && (
                <div className="mt-3 flex justify-center">
                  <span
                    className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-3 py-1 text-xs font-bold"
                    data-testid="waiting-msg"
                  >
                    Waiting for Result
                  </span>
                </div>
              )}

              {/* CTA */}
              <Link
                to="/previous-results"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-black text-white font-display font-bold text-sm py-2.5 hover:bg-neutral-800 transition-colors"
                data-testid="cta-check-previous"
              >
                <span>Check Previous Results</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* ============ 3 FEATURE CARDS ============ */}
        <section className="max-w-md mx-auto sm:max-w-2xl lg:max-w-4xl px-4 sm:px-6 mt-6">
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <FeatureCard
              to="/common-numbers"
              title={<>COMMON<br />NUMBERS</>}
              tone="orange"
              IconComp={LightbulbIcon}
              testId="card-common-numbers"
            />
            <FeatureCard
              to="/previous-results"
              title={<>PREVIOUS<br />RESULTS</>}
              tone="blue"
              IconComp={ClipboardCheckIcon}
              testId="card-previous-results"
            />
            <FeatureCard
              to="/juwai-morning-teer"
              title={<>ABOUT<br />JUWAI TEER</>}
              tone="green"
              IconComp={OpenBookIcon}
              testId="card-info"
            />
          </div>
        </section>

        {/* ============ ABOUT INFO CARD ============ */}
        <section className="max-w-md mx-auto sm:max-w-2xl lg:max-w-4xl px-4 sm:px-6 mt-6">
          <div
            className="rounded-2xl bg-white border border-black/5 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.10)] p-5 flex gap-4"
            data-testid="info-block"
          >
            <div className="shrink-0">
              <TargetLogo className="w-14 h-14" />
            </div>
            <div>
              <h2 className="font-display font-black text-[#F04E1E] text-lg">
                About Juwai Morning Teer
              </h2>
              <p className="mt-2 text-slate-700 text-sm leading-relaxed">
                Juwai Morning Teer is a traditional archery based number
                activity associated with Jowai, Meghalaya. Results are declared
                twice each morning: <strong>First Round (F/R)</strong> at{" "}
                <strong>10:30 AM</strong> and{" "}
                <strong>Second Round (S/R)</strong> at{" "}
                <strong>11:30 AM</strong>. Stay connected for accurate results
                and updates.
              </p>
            </div>
          </div>
        </section>

        {/* ============ EXPLORE MORE ============ */}
        <section className="max-w-md mx-auto sm:max-w-2xl lg:max-w-4xl px-4 sm:px-6 mt-6">
          <div className="relative">
            <span className="inline-block bg-black text-white font-display font-black tracking-wide px-4 py-2 rounded-t-xl rounded-br-xl text-sm">
              Explore More
            </span>
          </div>
          <div className="mt-3 rounded-2xl bg-white border border-black/5 p-3 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.10)]">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3">
              <ExploreItem
                to="/historical-results"
                label={<>MONTHLY<br />RESULTS</>}
                Icon={CalendarDays}
                tint="#F04E1E"
                testId="explore-monthly"
              />
              <ExploreItem
                to="/historical-results"
                label={<>HISTORICAL<br />RESULTS</>}
                Icon={History}
                tint="#5B3FBF"
                testId="card-historical"
              />
              <ExploreItem
                to="/juwai-morning-teer"
                label={<>DREAM<br />NUMBERS</>}
                Icon={Star}
                tint="#E91E63"
                testId="explore-dream"
              />
              <ExploreItem
                to="/historical-results"
                label={<>STATISTICS<br />& ANALYSIS</>}
                Icon={LineChart}
                tint="#0F9C8B"
                testId="explore-stats"
              />
              <ExploreItem
                to="/juwai-morning-teer"
                label={<>JUWAI TEER<br />INFORMATION</>}
                Icon={InfoIcon}
                tint="#F6A417"
                testId="explore-info"
              />
            </div>
          </div>
        </section>

        {/* ============ SEO CONTENT: About Juwai Morning Teer ============ */}
        <section
          className="max-w-md mx-auto sm:max-w-2xl lg:max-w-4xl px-4 sm:px-6 mt-8"
          data-testid="home-seo-content"
        >
          <article className="rounded-2xl bg-white border border-black/5 p-5 sm:p-6 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.08)]">
            <h2 className="font-display font-black text-black text-2xl">
              Juwai Morning Teer Result Today
            </h2>
            <p className="mt-3 text-slate-700 text-[15px] leading-relaxed">
              Welcome to <strong>Juwai Morning Teer</strong>, an informational
              reference for the traditional archery-based number activity
              followed by many people across the North East of India. On this
              website you can check the latest{" "}
              <strong>Juwai Morning Teer Result</strong> for today along with
              the previous days' F/R and S/R numbers, updated manually by our
              editorial team.
            </p>
            <p className="mt-3 text-slate-700 text-[15px] leading-relaxed">
              The <strong>Juwai Teer Morning Result</strong> is declared in two
              rounds. The First Round (F/R) is generally announced around{" "}
              <strong>10:30 AM</strong> and the Second Round (S/R) around{" "}
              <strong>11:30 AM</strong>. Each result is a two-digit number
              between 00 and 99. Because our data is manually entered, you get
              a clean, single reference point instead of scattered numbers from
              unofficial sources.
            </p>
            <h3 className="font-display font-black text-black text-lg mt-5">
              How to check today's result
            </h3>
            <p className="mt-2 text-slate-700 text-[15px] leading-relaxed">
              As soon as the day's rounds finish, our team publishes the F/R
              and S/R numbers on the result board above. If a result has not
              been published yet, the board will show{" "}
              <em>Waiting for Result</em>. You can also browse the{" "}
              <a href="/previous-results" className="text-[#F04E1E] font-semibold">Previous Results</a>{" "}
              archive to review historical figures by date.
            </p>
            <h3 className="font-display font-black text-black text-lg mt-5">
              Common searches this page answers
            </h3>
            <p className="mt-2 text-slate-700 text-[15px] leading-relaxed">
              Whether you searched for <em>Juwai Morning Teer</em>,{" "}
              <em>Morning Teer Juwai</em>, <em>Juwai Teer Result</em>,{" "}
              <em>Juwai Morning Teer Result Today</em>,{" "}
              <em>Morning Juwai Teer</em>, or{" "}
              <em>Juwai Teer Morning Result</em> — you are on the right page.
              Every variation refers to the same daily two-round result board,
              and this page is our single source of truth for it.
            </p>
          </article>
        </section>

        {/* ============ FAQ ============ */}
        <section className="max-w-md mx-auto sm:max-w-2xl lg:max-w-4xl px-4 sm:px-6 mt-6" data-testid="home-faq">
          <h2 className="font-display font-black text-black text-2xl px-1">
            Frequently Asked Questions
          </h2>
          <FaqList
            testIdPrefix="home-faq"
            faqs={[
              {
                q: "What time is the Juwai Morning Teer result declared?",
                a: "The First Round (F/R) is generally declared around 10:30 AM and the Second Round (S/R) around 11:30 AM (Asia/Kolkata timezone). Actual publish time depends on when the rounds finish and the admin updates the result.",
              },
              {
                q: "What is the difference between F/R and S/R in Juwai Teer?",
                a: "F/R stands for First Round and S/R stands for Second Round. Each is an independent two-digit number (00–99) recorded from the day's archery rounds.",
              },
              {
                q: "Is this the official Juwai Morning Teer website?",
                a: "No. Juwai Morning Teer is an independent informational website. We publish reference results manually and do not operate, organize, promote or facilitate any wagering activity.",
              },
              {
                q: "How often are results and common numbers updated?",
                a: "Every day, after each round finishes. If today's result has not been published yet, the homepage displays 'Waiting for Result' until our team updates it.",
              },
              {
                q: "Where can I check previous Juwai Morning Teer results?",
                a: "Use the Previous Results page to browse a full archive of dated F/R and S/R figures, newest first.",
              },
              {
                q: "Are common numbers guaranteed to be correct?",
                a: "No. Common numbers are reference figures only and carry no guarantee. They should not be treated as predictions.",
              },
            ]}
          />
          <FaqSchema
            id="home"
            faqs={[
              { q: "What time is the Juwai Morning Teer result declared?", a: "F/R around 10:30 AM and S/R around 11:30 AM (Asia/Kolkata)." },
              { q: "What is the difference between F/R and S/R in Juwai Teer?", a: "F/R is the First Round and S/R is the Second Round, each a two-digit number." },
              { q: "Is this the official Juwai Morning Teer website?", a: "No, this is an independent informational website." },
              { q: "How often are results updated?", a: "Every day, after each round finishes." },
              { q: "Where can I check previous results?", a: "On the Previous Results page." },
              { q: "Are common numbers guaranteed?", a: "No, they are reference figures only." },
            ]}
          />
          <OrganizationAlt />
        </section>

        {/* ============ DISCLAIMER ============ */}
        <section
          className="max-w-md mx-auto sm:max-w-2xl lg:max-w-4xl px-4 sm:px-6 mt-6 mb-10"
          data-testid="home-disclaimer"
        >
          <div className="rounded-2xl bg-[#FBEEDC] border border-[#F1D9B5] p-4 flex gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#8B5A2B] grid place-items-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <div className="text-sm text-[#5A3A19] leading-relaxed">
              <div className="font-display font-black text-[#5A3A19] mb-0.5">
                Disclaimer
              </div>
              This website is for informational and entertainment purpose only.
              We do not promote or encourage any kind of gambling. Teer result
              is not guaranteed.
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
